/**
 * Tracks how deep the current browser history entry is inside THIS site, so "go back" can tell a
 * previous page of ours from a previous page of some other site.
 *
 * Every entry is stamped with a depth in `history.state`: the page the parker landed on is 0, and
 * each client-side push adds one. A replace keeps the current depth. Because the number lives on
 * the history entry itself, it is right after Back, Forward and a reload, not just after pushes.
 *
 * ## Invariants:
 * - `history.length` is NOT used. Why: it counts every entry in the tab, including other sites,
 *   so a parker who arrived from a search engine or an email would be sent back off the site.
 * - The depth is merged into the state object the caller passed, never replacing it. Why: the
 *   Next.js router keeps its own keys in `history.state` and reads them on Back.
 * - An entry with no depth reads as 0 (not ours to go back to). Why: failing towards "go home"
 *   is safe; failing towards `router.back()` can leave the site.
 */

const DEPTH_KEY = '__pbInAppDepth';

let installed = false;

function readDepth(state: unknown): number | null {
  if (state && typeof state === 'object' && DEPTH_KEY in state) {
    const value = (state as Record<string, unknown>)[DEPTH_KEY];
    return typeof value === 'number' ? value : null;
  }
  return null;
}

function withDepth(data: unknown, depth: number): unknown {
  if (data === null || data === undefined) return { [DEPTH_KEY]: depth };
  if (typeof data === 'object') return { ...(data as Record<string, unknown>), [DEPTH_KEY]: depth };
  // A primitive state cannot carry the stamp; leave it as the caller wrote it.
  return data;
}

/**
 * Wraps `history.pushState` / `replaceState` so each entry carries its depth, and stamps the
 * landing entry with 0. Safe to call more than once; only the first call installs.
 */
export function installInAppHistory(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const history = window.history;
  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);

  if (readDepth(history.state) === null) {
    originalReplace(withDepth(history.state, 0), '');
  }

  history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    const depth = (readDepth(history.state) ?? 0) + 1;
    originalPush(withDepth(data, depth), unused, url);
  };
  history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    const depth = readDepth(history.state) ?? 0;
    originalReplace(withDepth(data, depth), unused, url);
  };
}

/** True when the entry before the current one is a page of this site. */
export function hasInAppHistory(): boolean {
  if (typeof window === 'undefined') return false;
  return (readDepth(window.history.state) ?? 0) > 0;
}

/** Test-only: forget that the wrappers were installed. */
export function resetInAppHistoryForTests(): void {
  installed = false;
}
